import { cn, getUrlBasedOnType } from '@/helpers/utils/functionality-utils';
import type { NotificationSocket } from '@/types/user/notification-types';
import type { AlarmsSocket } from '@/types/user/alarm-types';
import { Link } from 'react-router';
import { useTranslation } from '@/hooks/useTranslation';
import { useResponsive } from '@/hooks/useResponsive';

export const NotificationTemplate = ({
  isToastVisible,
  dismiss,
  notification,
}: {
  isToastVisible: boolean;
  dismiss: () => void;
  notification: NotificationSocket;
}) => {
  const { t } = useTranslation('common');
  const { isMd } = useResponsive();
  const category = notification.msg_category;
  const message = notification.msg;
  const moreInfo = notification.more_info;

  const categoryMap = {
    task: 'pi pi-wrench text-orange-600',
    notification: 'pi pi-bell text-blue-600',
  };

  const firstKey = Object.keys(moreInfo)?.[0];
  const firstId = firstKey ? Number(moreInfo[firstKey]) : null;

  let url = '';
  if (firstKey && firstId) {
    url = getUrlBasedOnType(firstKey, firstId);
  }

  return isMd ? (
    <div
      className={cn(
        `${
          isToastVisible ? 'animate-custom-enter' : 'animate-custom-leave'
        } pointer-events-auto flex w-full max-w-2xl rounded-lg bg-blue-50 shadow-pfs-7 ring-1 ring-black ring-opacity-5`,
        category === 'task' ? 'bg-green-50' : 'bg-blue-50',
      )}
    >
      <div className="flex-1 p-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 self-center pt-0.5">
            <i
              className={cn(
                'text-6xl text-indigo-600',
                categoryMap[category as keyof typeof categoryMap],
              )}
            ></i>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-2xl font-semibold uppercase text-gray-700">
              {category}
            </p>
            <p className="mt-1 text-xl text-gray-500">
              {url ? (
                <div className="flex items-center gap-2">
                  <Link
                    className="!text-gray-500 !underline !underline-offset-8 hover:!text-blue-600"
                    to={url}
                  >
                    {message}
                  </Link>
                  <Link
                    className="!text-surface-600 hover:!text-surface-400"
                    to={url}
                  >
                    <i className="pi pi-external-link"></i>
                  </Link>
                </div>
              ) : (
                message
              )}
            </p>
          </div>
        </div>
      </div>
      <div className="flex border-l border-gray-200 bg-white">
        <button
          onClick={() => dismiss()}
          className="flex w-full items-center justify-center rounded-none rounded-r-lg border border-transparent p-4 text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {t('close')}
        </button>
      </div>
    </div>
  ) : (
    <div
      className={cn(
        `${
          isToastVisible ? 'animate-custom-enter' : 'animate-custom-leave'
        } pointer-events-auto flex w-full max-w-2xl flex-col rounded-lg bg-blue-50 shadow-pfs-7 ring-1 ring-black ring-opacity-5`,
        category === 'task' ? 'bg-green-50' : 'bg-blue-50',
      )}
    >
      <div className="flex-1 p-4">
        <div className="flex flex-col items-start gap-4">
          <div className="flex-shrink-0 self-center pt-0.5">
            <i
              className={cn(
                'text-6xl text-indigo-600',
                categoryMap[category as keyof typeof categoryMap],
              )}
            ></i>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-2xl font-semibold uppercase text-gray-700">
              {category}
            </p>
            <p className="mt-1 text-xl text-gray-500">
              {url ? (
                <div className="flex items-center gap-2">
                  <Link
                    className="!underline !underline-offset-8 [&&]:!text-surface-600 [&&]:hover:!text-blue-600"
                    to={url}
                  >
                    {message}
                  </Link>
                  <Link
                    className="!text-surface-600 hover:text-blue-600"
                    to={url}
                  >
                    <i className="pi pi-external-link"></i>
                  </Link>
                </div>
              ) : (
                message
              )}
            </p>
          </div>
        </div>
      </div>
      <div className="flex border-l border-gray-200 bg-white">
        <button
          onClick={() => dismiss()}
          className="flex w-full items-center justify-center rounded-none rounded-r-lg border border-transparent p-4 text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {t('close')}
        </button>
      </div>
    </div>
  );
};

export const AlarmTemplate = ({
  isToastVisible,
  dismiss,
  notification,
}: {
  isToastVisible: boolean;
  dismiss: () => void;
  notification: AlarmsSocket;
}) => {
  const { t } = useTranslation('common');
  const { isMd } = useResponsive();
  const message = notification.message;
  const moreInfo = notification.more_info;
  const action = notification.action;

  const firstKey = Object.keys(moreInfo)?.[0];
  const firstId = firstKey ? Number(moreInfo[firstKey]) : null;

  let url = '';
  if (firstKey && firstId) {
    url = getUrlBasedOnType(firstKey, firstId);
  }
  return isMd ? (
    <div
      className={cn(
        `${
          isToastVisible ? 'animate-custom-enter' : 'animate-custom-leave'
        } pointer-events-auto flex w-full max-w-2xl rounded-lg bg-red-100 shadow-pfs-7 ring-1 ring-black ring-opacity-5`,
      )}
    >
      <div className="flex-1 p-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 self-center pt-0.5">
            <i className={cn('text-6xl text-red-600', 'pi pi-clock')}></i>
          </div>
          <div className="ml-3 flex-1">
            <p className="mt-1 text-xl text-gray-500">{message}</p>
            <div className="text-lg font-bold text-gray-400">{t('action')}</div>
            <p className="font-semibold uppercase text-gray-700">{action}</p>
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-center border-l border-gray-200 bg-white">
        <Link to={url}>
          <button
            type="button"
            onClick={() => dismiss()}
            className="flex w-full items-center justify-center rounded-none rounded-r-lg border border-transparent p-4 text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('details')}
          </button>
        </Link>
        <div className="w-[80%] self-center border-t border-gray-200" />
        <button
          onClick={() => dismiss()}
          className="flex w-full items-center justify-center rounded-none rounded-r-lg border border-transparent p-4 text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {t('dismiss')}
        </button>
      </div>
    </div>
  ) : (
    <div
      className={cn(
        `${
          isToastVisible ? 'animate-custom-enter' : 'animate-custom-leave'
        } pointer-events-auto flex w-full max-w-2xl flex-col rounded-lg bg-red-100 shadow-pfs-7 ring-1 ring-black ring-opacity-5`,
      )}
    >
      <div className="flex-1 p-4">
        <div className="flex flex-col items-start gap-4">
          <div className="flex-shrink-0 self-center pt-0.5">
            <i className={cn('text-6xl text-red-600', 'pi pi-clock')}></i>
          </div>
          <div className="ml-3 flex-1">
            <p className="mt-1 text-xl text-gray-500">{message}</p>
            <div className="text-lg font-bold text-gray-400">{t('action')}</div>
            <p className="font-semibold uppercase text-gray-700">{action}</p>
          </div>
        </div>
      </div>
      <div className="flex justify-between border-l border-gray-200 bg-white *:w-1/2">
        <Link to={url}>
          <button
            type="button"
            onClick={() => dismiss()}
            className="flex w-full items-center justify-center rounded-none rounded-r-lg border border-transparent p-4 text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('details')}
          </button>
        </Link>
        <button
          onClick={() => dismiss()}
          className="flex w-full items-center justify-center rounded-none rounded-r-lg border border-transparent p-4 text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {t('dismiss')}
        </button>
      </div>
    </div>
  );
};
